import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  Grid,
  Button,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  FormControl,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import api from "../services/api";
import { getToken } from "../services/auth";
import SaleInvoiceImport from "./SaleInvoiceImport";
import { todayISO } from "../utils/date";

/* ================= SALE INVOICE FORM ================= */
export default function SaleInvoiceForm({ openId, onConsumed }) {
  const [editId, setEditId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Opened from the "Needs Pricing" list — load that invoice straight into edit.
  useEffect(() => {
    if (openId) {
      setEditId(openId);
      onConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setImportOpen(true)}
        >
          Import from Excel
        </Button>
      </Box>

      <SaleInvoiceImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => setReloadKey((k) => k + 1)}
      />

      <SaleInvoiceEditor editId={editId} setEditId={setEditId} onSaved={() => setReloadKey((k) => k + 1)} />
      <SaleInvoiceList key={reloadKey} onEdit={setEditId} />
    </Box>
  );
}

function computeMasterPO(details) {
  const distinct = [
    ...new Set(
      (details || [])
        .map((d) => (d.PO || "").trim())
        .filter(Boolean)
    ),
  ];
  if (distinct.length === 0) return "";
  if (distinct.length === 1) return distinct[0];
  return "MULTIPLE";
}


/* ================= SALE INVOICE EDITOR ================= */
function SaleInvoiceEditor({ editId, setEditId, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [packingLists, setPackingLists] = useState([]); // active packing lists for Direct-mode import
  const [pickedPackingListId, setPickedPackingListId] = useState(null);

  const [master, setMaster] = useState({
    Master_ID: "",
    InvoiceType: "Direct",
    Customer_ID: "",
    Name: "",
    PO: "",
    Booking: "",
    Delivery_Terms: "FAS Houston",
    Notes: "",
    Date: todayISO(),
    Truck_No: "",
    Trailer_No: "",
    Seal_No: "",
    Truck_GrossWt: "",
    Truck_TareWt: "",
    Truck_NetWt: "",
  });

  const emptyDetail = useMemo(() => ({
    Item_ID: "",
    Item_Description: "",
    Container_No: "",
    Seal_No: "",
    Pallets: 0,
    Box: "",
    G_Wt: 0,
    T_Wt: 0,
    N_Wt: 0,
    Rate: 0,
    L_Amount: 0,
    PO: "",
  }), []);

  const [details, setDetails] = useState([{ ...emptyDetail }]);

  const isDirect = master.InvoiceType === "Direct";

  const resetForm = useCallback(() => {
    setMaster({
      Master_ID: "",
      InvoiceType: "Direct",
      Customer_ID: "",
      Name: "",
      PO: "",
      Booking: "",
      Delivery_Terms: "FAS Houston",
      Notes: "",
      Date: todayISO(),
      Truck_No: "",
      Trailer_No: "",
      Seal_No: "",
    });
    setDetails([{ ...emptyDetail }]);
  }, [emptyDetail]);

  /* ===== FETCH DROPDOWNS ===== */
  useEffect(() => {
    api.get("/saleInvoices/accounts")
      .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
      .catch(console.error);

    api.get("/saleInvoices/products")
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(console.error);
  }, []);

  /* ===== Active Packing Lists (refreshable) ===== */
  const loadActivePackingLists = useCallback(() => {
    api.get("/packingList/active")
      .then((r) => setPackingLists(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load packing lists:", e));
  }, []);
  useEffect(() => { loadActivePackingLists(); }, [loadActivePackingLists]);

  /* ===== Import details from a Packing List (Direct mode) ===== */
  const importPackingList = useCallback(async (pl) => {
    if (!pl?.Master_ID) return;
    try {
      const res = await api.get(`/packingList/${pl.Master_ID}`);
      const m = res.data?.master || {};
      const d = res.data?.details || [];

      // Pre-fill customer if not already set
      setMaster((prev) => ({
        ...prev,
        Customer_ID: prev.Customer_ID || String(m.Customer_ID || ""),
        Name: prev.Name || m.Customer_Name || "",
      }));

      // Map packing list details into invoice detail rows.
      // PackQty → invoice "Qty"; TotalWeight (Net) is brought into G_Wt/N_Wt
      // so weight-based Amount math kicks in. Tare stays 0 (finished goods
      // packing lists track net weight already).
      const newDetails = d.map((row) => {
        const qty   = Number(row.PackQty)     || 0;
        const netWt = Number(row.TotalWeight) || 0;
        const rate  = Number(row.Rate)        || 0;
        const basis = netWt > 0 ? netWt : qty;
        return {
          ...emptyDetail,
          Item_ID: String(row.Item_ID || ""),
          Item_Description: row.Item_Description || "",
          Pallets: qty,
          Box: row.PackType || "",
          G_Wt: netWt,
          T_Wt: 0,
          N_Wt: netWt,
          Rate: rate,
          L_Amount: +(basis * rate).toFixed(2),
        };
      });

      setDetails(newDetails.length ? newDetails : [{ ...emptyDetail }]);
      setPickedPackingListId(pl.Master_ID);
    } catch (err) {
      console.error("Failed to import packing list:", err);
      alert("Failed to import packing list: " + (err.response?.data?.error || err.message));
    }
  }, [emptyDetail]);

  /* ===== LOAD INVOICE FOR EDIT ===== */
  useEffect(() => {
    if (!editId) return;

    api.get(`/saleInvoices/${editId}`)
      .then((res) => {
        const data = res.data;
        const m = data.master || {};
        setMaster({
          Master_ID: m.Master_ID || "",
          InvoiceType: m.InvoiceType || (m.Booking ? "Container" : "Direct"),
          Customer_ID: String(m.Customer_ID || ""),
          Name: m.Name || "",
          PO: m.PO || "",
          Booking: m.Booking || "",
          Delivery_Terms: m.Delivery_Terms || m.d_terms || "FAS Houston",
          Notes: m.Notes || "",
          Date: m.Date ? m.Date.split("T")[0] : "",
          Truck_No: m.Truck_No || "",
          Trailer_No: m.Trailer_No || "",
          Seal_No: m.Seal_No || "",
          Truck_GrossWt: m.Truck_GrossWt ?? "",
          Truck_TareWt: m.Truck_TareWt ?? "",
          Truck_NetWt: m.Truck_NetWt ?? "",
        });

        setDetails(
          (data.details || []).map((d) => ({
            ...emptyDetail,
            ...d,
            Item_ID: String(d.Item_ID || ""),
            Container_No: d.Container || d.Container_No || "",
            Seal_No: d.Seal || d.Seal_No || "",
            G_Wt: Number(d.G_Wt) || 0,
            T_Wt: Number(d.T_Wt) || 0,
            N_Wt: Number(d.N_Wt) || 0,
            Rate: Number(d.Price ?? d.Rate) || 0,
            L_Amount: Number(d.NetAmount ?? d.L_Amount) || 0,
            PO: d.PO || "",
          }))
        );

        // Remember a previously-linked packing list so re-saving keeps the link
        // (and the bundle keeps including its PDF).
        setPickedPackingListId(m.PackingList_Master_ID || null);
      })
      .catch((err) => {
        console.error("Failed to load invoice:", err);
        resetForm();
      });
  }, [editId, emptyDetail, resetForm]);

  /* ===== BOOKING AUTO LOOKUP (Container only) ===== */
  const fetchBookingDetails = useCallback(
    async (bookingNo) => {
      if (!bookingNo || master.InvoiceType !== "Container") return;
      try {
        const res = await api.get(`/saleInvoices/booking/${encodeURIComponent(bookingNo)}`);
        const data = res.data;
        if (!Array.isArray(data) || !data.length) return;

        const firstWithCustomer = data.find((d) => d.Customer_ID) || data[0];

        const newDetails = data.map((d) => ({
          ...emptyDetail,
          Item_ID: String(d.Item_ID || ""),
          Item_Description: d.Item_Description || "",
          Container_No: d.ContainerNo || "",
          Seal_No: d.SealNo || "",
          Rate: Number(d.Price) || 0,
          PO: d.PO || "",
        }));

        const finalDetails = newDetails.length ? newDetails : [{ ...emptyDetail }];

        setMaster((prev) => ({
          ...prev,
          Booking: bookingNo,
          Customer_ID: String(firstWithCustomer.Customer_ID || ""),
          Name: firstWithCustomer.Name || "",
          PO: computeMasterPO(finalDetails),
        }));
        setDetails(finalDetails);
      } catch (err) {
        console.error("Booking lookup failed:", err);
      }
    },
    [emptyDetail, master.InvoiceType]
  );

  // A "charge" product (e.g. Delivery) carries a flat dollar amount with no
  // weight × rate. Identified by its product group so new charge types can be
  // added in the catalog without code changes. (Matches PurchaseInvoiceForm.)
  const isChargeProduct = (itemId) =>
    products.find((p) => String(p.id) === String(itemId))?.Group_ID === "CHARGES";

  /* ===== UPDATE DETAIL ROW ===== */
  const updateRow = (i, field, value) => {
    const rows = [...details];
    if (["Item_Description", "Container_No", "Seal_No", "Box", "PO"].includes(field)) {
      rows[i][field] = value || "";
    } else if (field === "Item_ID") {
      rows[i][field] = value || "";
      // Switching to a charge item: zero out weight/rate so the flat amount is
      // the only thing entered. Skip the weight × rate recompute below.
      if (isChargeProduct(value)) {
        rows[i].G_Wt = 0; rows[i].T_Wt = 0; rows[i].N_Wt = 0; rows[i].Rate = 0;
        setDetails(rows);
        return;
      }
    } else if (field === "L_Amount") {
      // Direct amount entry, only used by charge rows.
      rows[i].L_Amount = value === "" ? 0 : parseFloat(value);
      setDetails(rows);
      return;
    } else {
      rows[i][field] = value === "" ? 0 : parseFloat(value);
    }

    // A charge row keeps its typed L_Amount — don't overwrite with weight math.
    if (isChargeProduct(rows[i].Item_ID)) {
      setDetails(rows);
      return;
    }

    if (isDirect) {
      // Direct: weight-based. Net = Gross - Tare, Amount = Net × Rate.
      const gross = parseFloat(rows[i].G_Wt) || 0;
      let tare    = parseFloat(rows[i].T_Wt) || 0;
      if (tare > gross) { tare = gross; rows[i].T_Wt = gross; }
      rows[i].N_Wt = +(gross - tare).toFixed(2);

      const rate = parseFloat(rows[i].Rate) || 0;
      rows[i].L_Amount = +(rows[i].N_Wt * rate).toFixed(2);
    } else {
      // Container: weight-based
      const pallets = parseFloat(rows[i].Pallets) || 0;
      if (rows[i].Box === "Box") rows[i].T_Wt = +(pallets * 75).toFixed(2);
      else if (rows[i].Box === "Pallets") rows[i].T_Wt = +(pallets * 50).toFixed(2);
      else rows[i].T_Wt = 0;

      if (rows[i].T_Wt > rows[i].G_Wt) rows[i].T_Wt = rows[i].G_Wt;
      rows[i].N_Wt = +(rows[i].G_Wt - rows[i].T_Wt).toFixed(2);
      rows[i].L_Amount = +(rows[i].N_Wt * rows[i].Rate).toFixed(2);
    }

    setDetails(rows);
    if (!isDirect) {
      setMaster((prev) => ({ ...prev, PO: computeMasterPO(rows) }));
    }
  };

  const addRow = () => setDetails((prev) => [...prev, { ...emptyDetail }]);
  const removeRow = (i) => {
    setDetails((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      return next.length ? next : [{ ...emptyDetail }];
    });
  };

  const handleTypeChange = (newType) => {
    setMaster((prev) => ({
      ...prev,
      InvoiceType: newType,
      // Clear container-only fields when switching to Direct (Delivery Terms is
      // a Container/export incoterm — not used on a Direct pickup sale).
      ...(newType === "Direct" ? { Booking: "", PO: "", Delivery_Terms: "" } : {}),
      // Clear truck-only fields when switching to Container
      ...(newType === "Container" ? { Truck_No: "", Trailer_No: "", Seal_No: "" } : {}),
    }));
    setDetails([{ ...emptyDetail }]);
  };

  const saveInvoice = async () => {
    // Client-side guards — the server rejects these but with a generic error.
    // Catch them here so the user sees exactly what's missing.
    const problems = [];
    if (!master.Date) problems.push("Date is required");
    if (!master.Name || !master.Customer_ID) problems.push("Customer is required");
    if (!details.length) problems.push("Add at least one item");
    if (problems.length) {
      alert("Please fix:\n• " + problems.join("\n• "));
      return;
    }

    try {
      // Persist the linked packing list (Direct sales only) so the bundle ZIP
      // can include its PDF later.
      const payload = {
        master: { ...master, PackingList_Master_ID: isDirect ? (pickedPackingListId || null) : null },
        details,
      };
      const res = editId
        ? await api.put(`/saleInvoices/${editId}`, payload)
        : await api.post("/saleInvoices", payload);

      // If this invoice consumed a packing list, mark it inactive so it stops
      // showing in the active dropdown — best-effort, never blocks the save.
      if (pickedPackingListId) {
        try {
          await api.post(`/packingList/${pickedPackingListId}/consume`);
        } catch (consumeErr) {
          console.warn("Failed to mark packing list as used:", consumeErr);
        }
        setPickedPackingListId(null);
        loadActivePackingLists();
      }

      alert(res.data?.message || "Saved successfully");
      resetForm();
      setEditId(null);
      onSaved?.();
    } catch (err) {
      console.error("Save failed:", err);
      const data = err.response?.data;
      const detail = Array.isArray(data?.issues) && data.issues.length
        ? "\n• " + data.issues.map((i) => `${i.path || "(root)"}: ${i.message}`).join("\n• ")
        : "";
      alert("Failed to save invoice: " + (data?.error || err.message) + detail);
    }
  };

  return (
    <Box p={1} sx={{ width: "100%", overflowX: "auto" }}>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6">{editId ? "Edit Sale Invoice" : "Add Sale Invoice"}</Typography>

        {/* ===== Invoice type toggle ===== */}
        <FormControl sx={{ mt: 1 }}>
          <FormLabel sx={{ fontSize: "0.875rem" }}>Invoice Type</FormLabel>
          <RadioGroup
            row
            value={master.InvoiceType}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <FormControlLabel value="Direct" control={<Radio />} label="Direct Sale" />
            <FormControlLabel value="Container" control={<Radio />} label="Container Shipment" />
          </RadioGroup>
        </FormControl>

        {/* ===== Master fields ===== */}
        <Grid container spacing={2} mt={0}>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              type="date"
              label="Date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={master.Date}
              onChange={(e) => setMaster({ ...master, Date: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 8, md: isDirect ? 6 : 6 }}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(o) => o.SubAccount_Name || ""}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={accounts.find((a) => String(a.id) === String(master.Customer_ID)) || null}
              onChange={(e, v) => setMaster({ ...master, Customer_ID: String(v?.id || ""), Name: v?.SubAccount_Name || "" })}
              renderInput={(params) => <TextField {...params} label="Customer" fullWidth />}
              fullWidth
              sx={{ width: "100%", minWidth: { xs: "100%", sm: 280 } }}
            />
          </Grid>

          {!isDirect && (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  label="Delivery Terms"
                  fullWidth
                  value={master.Delivery_Terms || ""}
                  onChange={(e) => setMaster({ ...master, Delivery_Terms: e.target.value })}
                  helperText="Defaults to FAS Houston — change only when different"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 6, md: 2 }}>
                <TextField
                  label="Booking"
                  value={master.Booking || ""}
                  onChange={(e) => setMaster({ ...master, Booking: e.target.value })}
                  onBlur={(e) => fetchBookingDetails(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchBookingDetails(e.currentTarget.value); } }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 6, md: 2 }}>
                <TextField
                  label="PO"
                  value={master.PO || ""}
                  InputProps={{ readOnly: true }}
                  helperText={master.PO === "MULTIPLE" ? "Per-line POs in table" : " "}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>

        {/* ===== Load from Packing List (Direct mode, customer-filtered) =====
            Banner appears only when:
              - mode is Direct
              - a customer has been selected
              - at least one active packing list exists for that customer */}
        {isDirect && master.Customer_ID && (() => {
          const customerLists = packingLists.filter(
            (p) => String(p.Customer_ID || "") === String(master.Customer_ID)
          );
          if (!customerLists.length) return null;

          return (
            <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: "#f5faff", border: 1, borderColor: "primary.light" }}>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">
                  📦 Found {customerLists.length} active packing list{customerLists.length === 1 ? "" : "s"} for{" "}
                  <b>{master.Name || "this customer"}</b>
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
                  — click one to auto-fill items
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {customerLists.map((pl) => {
                  const ref   = pl.PackingList_No ? ` · ${pl.PackingList_No}` : "";
                  const date  = pl.Date ? ` · ${String(pl.Date).split("T")[0]}` : "";
                  const qty   = Number(pl.TotalPackQty) || 0;
                  const wt    = Number(pl.TotalWeight)  || 0;
                  const stats = ` · ${qty.toFixed(2)} pcs · ${wt.toFixed(2)} wt`;
                  const isPicked = pl.Master_ID === pickedPackingListId;

                  return (
                    <Chip
                      key={pl.Master_ID}
                      label={`#${pl.Master_ID}${ref}${date}${stats}`}
                      onClick={() => importPackingList(pl)}
                      color={isPicked ? "primary" : "default"}
                      variant={isPicked ? "filled" : "outlined"}
                      sx={{ cursor: "pointer" }}
                    />
                  );
                })}
              </Box>

              {pickedPackingListId && (
                <Typography variant="caption" sx={{ mt: 1, display: "block", color: "primary.main" }}>
                  ✓ Loaded — will be marked used after the invoice is saved
                </Typography>
              )}
            </Box>
          );
        })()}

        {/* ===== Truck delivery (Direct mode only, optional) ===== */}
        {isDirect && (
          <Accordion
            sx={{ mt: 2, boxShadow: "none", border: 1, borderColor: "divider" }}
            defaultExpanded={!!(master.Truck_No || master.Trailer_No || master.Seal_No)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Truck Delivery <span style={{ color: "#888", fontWeight: 400 }}>(optional)</span>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Truck #"
                    fullWidth
                    value={master.Truck_No || ""}
                    onChange={(e) => setMaster({ ...master, Truck_No: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Trailer #"
                    fullWidth
                    value={master.Trailer_No || ""}
                    onChange={(e) => setMaster({ ...master, Trailer_No: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Seal #"
                    fullWidth
                    value={master.Seal_No || ""}
                    onChange={(e) => setMaster({ ...master, Seal_No: e.target.value })}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* ===== Truck Scale Weights (both Direct & Container) ===== */}
        <Accordion
          sx={{ mt: 2, boxShadow: "none", border: 1, borderColor: "divider" }}
          defaultExpanded={!!(master.Truck_GrossWt || master.Truck_TareWt || master.Truck_NetWt)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Truck Scale Weights <span style={{ color: "#888", fontWeight: 400 }}>(optional)</span>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  type="number" label="Truck Gross Wt (lb)" fullWidth
                  value={master.Truck_GrossWt ?? ""}
                  onChange={(e) => {
                    const g = e.target.value;
                    const net = (Number(g) || 0) - (Number(master.Truck_TareWt) || 0);
                    setMaster({ ...master, Truck_GrossWt: g, Truck_NetWt: net ? net : "" });
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  type="number" label="Truck Tare Wt (lb)" fullWidth
                  value={master.Truck_TareWt ?? ""}
                  onChange={(e) => {
                    const t = e.target.value;
                    const net = (Number(master.Truck_GrossWt) || 0) - (Number(t) || 0);
                    setMaster({ ...master, Truck_TareWt: t, Truck_NetWt: net ? net : "" });
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  type="number" label="Truck Net Wt (lb)" fullWidth
                  helperText="Auto = Gross − Tare (editable)"
                  value={master.Truck_NetWt ?? ""}
                  onChange={(e) => setMaster({ ...master, Truck_NetWt: e.target.value })}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ===== Items table ===== */}
        <TableContainer sx={{ overflowX: "auto", mt: 2 }}>
          {isDirect ? <DirectItemsTable details={details} products={products} updateRow={updateRow} removeRow={removeRow} />
                    : <ContainerItemsTable details={details} products={products} updateRow={updateRow} removeRow={removeRow} />}
        </TableContainer>

        <Box mt={2}>
          <Button onClick={addRow}>+ Add Item</Button>
          <Button variant="contained" sx={{ ml: 2 }} onClick={saveInvoice}>{editId ? "Update" : "Save"}</Button>
        </Box>
      </Paper>
    </Box>
  );
}

/* ================= DIRECT MODE ITEMS TABLE ================= */
function DirectItemsTable({ details, products, updateRow, removeRow }) {
  const totalGross  = details.reduce((s, r) => s + (Number(r.G_Wt)     || 0), 0);
  const totalTare   = details.reduce((s, r) => s + (Number(r.T_Wt)     || 0), 0);
  const totalNet    = details.reduce((s, r) => s + (Number(r.N_Wt)     || 0), 0);
  const totalAmount = details.reduce((s, r) => s + (Number(r.L_Amount) || 0), 0);
  return (
    <Table size="small" sx={{ minWidth: 800 }}>
      <TableHead>
        <TableRow>
          <TableCell>Item</TableCell>
          <TableCell>PO</TableCell>
          <TableCell>Gross</TableCell>
          <TableCell>Tare</TableCell>
          <TableCell>Net</TableCell>
          <TableCell>Rate</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {details.map((r, i) => {
          const isCharge =
            products.find((p) => String(p.id) === String(r.Item_ID))?.Group_ID === "CHARGES";
          return (
          <TableRow key={i}>
            <TableCell>
              <Autocomplete
                options={products}
                getOptionLabel={(o) => o.Product_Description || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={products.find((p) => String(p.id) === String(r.Item_ID)) || null}
                onChange={(e, v) => {
                  updateRow(i, "Item_ID", String(v?.id || ""));
                  updateRow(i, "Item_Description", v?.Product_Description || "");
                }}
                renderInput={(params) => <TextField {...params} size="small" />}
                sx={{ width: "100%", minWidth: 200 }}
              />
            </TableCell>
            <TableCell>
              <TextField size="small" sx={{ width: 100 }}
                value={r.PO || ""} onChange={(e) => updateRow(i, "PO", e.target.value)} />
            </TableCell>
            {isCharge ? (
              <>
                <TableCell colSpan={3} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  Flat charge (no weight)
                </TableCell>
                <TableCell />
                <TableCell>
                  <TextField size="small" type="number" sx={{ width: 90 }}
                    value={r.L_Amount}
                    onChange={(e) => updateRow(i, "L_Amount", e.target.value)}
                    inputProps={{ step: "0.01", min: "0" }} />
                </TableCell>
              </>
            ) : (
              <>
                <TableCell>
                  <TextField size="small" type="number" sx={{ width: 90 }}
                    value={r.G_Wt} onChange={(e) => updateRow(i, "G_Wt", e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="number" sx={{ width: 90 }}
                    value={r.T_Wt} onChange={(e) => updateRow(i, "T_Wt", e.target.value)} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{(Number(r.N_Wt) || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <TextField size="small" type="number" sx={{ width: 90 }}
                    value={r.Rate} onChange={(e) => updateRow(i, "Rate", e.target.value)} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{(r.L_Amount || 0).toFixed(2)}</TableCell>
              </>
            )}
            <TableCell>
              <Button color="error" size="small" onClick={() => removeRow(i)}>X</Button>
            </TableCell>
          </TableRow>
          );
        })}
        <TableRow sx={{ bgcolor: "action.hover" }}>
          <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
          <TableCell />
          <TableCell sx={{ fontWeight: 700 }}>{totalGross.toFixed(2)}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{totalTare.toFixed(2)}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{totalNet.toFixed(2)}</TableCell>
          <TableCell />
          <TableCell sx={{ fontWeight: 700 }}>{totalAmount.toFixed(2)}</TableCell>
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  );
}

/* ================= CONTAINER MODE ITEMS TABLE ================= */
function ContainerItemsTable({ details, products, updateRow, removeRow }) {
  const totalPallets = details.reduce((s, r) => s + (Number(r.Pallets)  || 0), 0);
  const totalGross   = details.reduce((s, r) => s + (Number(r.G_Wt)     || 0), 0);
  const totalTare    = details.reduce((s, r) => s + (Number(r.T_Wt)     || 0), 0);
  const totalNet     = details.reduce((s, r) => s + (Number(r.N_Wt)     || 0), 0);
  const totalAmount  = details.reduce((s, r) => s + (Number(r.L_Amount) || 0), 0);
  return (
    <Table size="small" sx={{ minWidth: 1000 }}>
      <TableHead>
        <TableRow>
          <TableCell>Container #</TableCell>
          <TableCell>Seal #</TableCell>
          <TableCell>PO</TableCell>
          <TableCell>Item</TableCell>
          <TableCell>Pallets</TableCell>
          <TableCell>Box</TableCell>
          <TableCell>Gross</TableCell>
          <TableCell>Tare</TableCell>
          <TableCell>Net</TableCell>
          <TableCell>Rate</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {details.map((r, i) => (
          <TableRow key={i}>
            <TableCell><TextField size="small" sx={{ width: 130 }} value={r.Container_No} onChange={(e) => updateRow(i, "Container_No", e.target.value)} /></TableCell>
            <TableCell><TextField size="small" sx={{ width: 100 }} value={r.Seal_No} onChange={(e) => updateRow(i, "Seal_No", e.target.value)} /></TableCell>
            <TableCell><TextField size="small" sx={{ width: 90 }} value={r.PO || ""} onChange={(e) => updateRow(i, "PO", e.target.value)} /></TableCell>
            <TableCell>
              <Autocomplete
                options={products}
                getOptionLabel={(o) => o.Product_Description || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={products.find((p) => String(p.id) === String(r.Item_ID)) || null}
                onChange={(e, v) => {
                  updateRow(i, "Item_ID", String(v?.id || ""));
                  updateRow(i, "Item_Description", v?.Product_Description || "");
                }}
                renderInput={(params) => <TextField {...params} size="small" />}
                sx={{ width: "100%", minWidth: 160 }}
              />
            </TableCell>
            <TableCell><TextField size="small" sx={{ width: 70 }} type="number" value={r.Pallets} onChange={(e) => updateRow(i, "Pallets", e.target.value)} /></TableCell>
            <TableCell>
              <TextField select size="small" sx={{ width: 100 }} value={r.Box} onChange={(e) => updateRow(i, "Box", e.target.value)} SelectProps={{ native: true }}>
                <option value="">Select</option>
                <option value="Box">Box</option>
                <option value="Pallets">Pallets</option>
              </TextField>
            </TableCell>
            <TableCell><TextField size="small" sx={{ width: 80 }} type="number" value={r.G_Wt} onChange={(e) => updateRow(i, "G_Wt", e.target.value)} /></TableCell>
            <TableCell>{r.T_Wt}</TableCell>
            <TableCell>{r.N_Wt}</TableCell>
            <TableCell><TextField size="small" sx={{ width: 80 }} type="number" value={r.Rate} onChange={(e) => updateRow(i, "Rate", e.target.value)} /></TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{(r.L_Amount || 0).toFixed(2)}</TableCell>
            <TableCell><Button color="error" size="small" onClick={() => removeRow(i)}>X</Button></TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ bgcolor: "action.hover" }}>
          <TableCell colSpan={4} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{totalPallets.toFixed(2)}</TableCell>
          <TableCell />
          <TableCell sx={{ fontWeight: 700 }}>{totalGross.toFixed(2)}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{totalTare.toFixed(2)}</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>{totalNet.toFixed(2)}</TableCell>
          <TableCell />
          <TableCell sx={{ fontWeight: 700 }}>{totalAmount.toFixed(2)}</TableCell>
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  );
}

/* ================= SALE INVOICE LIST ================= */
export function SaleInvoiceList({ onEdit }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const today = todayISO();

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  /* The dates start at today, so typing a customer's name found nothing
   * unless the range was widened first - which made the Customer box look
   * broken. With this on, the dates are ignored and you get everything that
   * customer has ever had. */
  const [allDates, setAllDates] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All"); // All | Open | Partial | Paid

  const fetchRows = useCallback(() => {
    api.get("/saleInvoices", { params: allDates ? { name: search } : { name: search, fromDate, toDate } })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Failed to load sale invoices:", err));
  }, [search, fromDate, toDate, allDates]);

  useEffect(() => fetchRows(), [fetchRows]);

  const filtered = useMemo(
    () => statusFilter === "All"
      ? rows
      : rows.filter((r) => (r.PaymentStatus || "Open") === statusFilter),
    [rows, statusFilter]
  );

  const totals = useMemo(() => {
    let t = 0, p = 0, b = 0;
    for (const r of filtered) {
      t += Number(r.TotalAmount) || 0;
      p += Number(r.PaidAmount)  || 0;
      b += Number(r.Balance)     || 0;
    }
    return { total: t, paid: p, balance: b };
  }, [filtered]);

  const paymentChip = (s) => {
    const status = s || "Open";
    const color = status === "Paid" ? "success" : status === "Partial" ? "warning" : "error";
    return <Chip size="small" label={status} color={color} />;
  };

  const del = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/saleInvoices/${id}`);
      setRows((prev) => prev.filter((x) => x.Master_ID !== id));
      alert("Deleted successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to delete: " + (err.response?.data?.error || err.message));
    }
  };

  const archivePhotos = async (id) => {
    if (!window.confirm(
      "Archive photos for this invoice?\n\nMake sure you have already downloaded the Bundle ZIP and saved it.\n" +
      "This combines each container's photos into collages and DELETES the original photos from the server."
    )) return;
    try {
      const res = await api.post(`/saleInvoices/${id}/archive-photos`);
      alert(res.data?.message || "Archived");
    } catch (err) {
      console.error(err);
      alert("Archive failed: " + (err.response?.data?.error || err.message));
    }
  };

  const openPdf = (path) => {
    const token = getToken();
    const base = api.defaults.baseURL || "";
    const sep = path.includes("?") ? "&" : "?";
    const url = `${base}${path}${token ? `${sep}token=${encodeURIComponent(token)}` : ""}`;
    const w = window.open(url, "_blank");
    if (w) w.focus();
    else alert("Popup blocked. Allow popups for this site.");
  };

  const typeChip = (r) => {
    const type = r.InvoiceType || "Container";
    if (type === "Container") return <Chip size="small" label="Container" color="primary" variant="outlined" />;
    const hasTruck = r.Truck_No || r.Trailer_No;
    return <Chip size="small" label={hasTruck ? "Direct + Truck" : "Direct"} color={hasTruck ? "warning" : "default"} variant="outlined" />;
  };

  const statusChip = (r) =>
    r.ShipmentClosed
      ? <Chip size="small" label="✓ Closed" color="success" variant="outlined" sx={{ ml: 0.5 }} />
      : null;

  return (
    <Box p={1}>
      <Typography variant="h6" sx={{ color: "black" }}>Sale Invoices</Typography>

      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField label="Customer" fullWidth value={search} onChange={(e) => setSearch(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth disabled={allDates} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth disabled={allDates} value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={allDates} onChange={(e) => setAllDates(e.target.checked)} />}
              label="All dates"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <TextField select fullWidth label="Status"
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Partial">Partial</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 8, md: 2 }}>
            <Button variant="contained" fullWidth onClick={fetchRows}>Search</Button>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
          <Chip variant="outlined" label={`${filtered.length} invoice${filtered.length === 1 ? "" : "s"}`} />
          <Chip variant="outlined" color="primary" label={`Total: $${totals.total.toFixed(2)}`} />
          <Chip variant="outlined" color="success" label={`Paid: $${totals.paid.toFixed(2)}`} />
          <Chip variant="outlined" color="error"   label={`Open Balance: $${totals.balance.toFixed(2)}`} />
        </Box>
      </Paper>

      {(() => {
        const rowActions = (r) => (
          <>
            <Button size="small" onClick={() => onEdit(r.Master_ID)}>Edit</Button>
            <Button size="small" color="error" onClick={() => del(r.Master_ID)}>Delete</Button>
            <Button size="small" color="success" onClick={() => openPdf(`/saleInvoices/${r.Master_ID}/pdf`)}>View</Button>
            {(r.InvoiceType || "Container") === "Container" && (
              <Button size="small" color="warning" onClick={() => openPdf(`/saleInvoices/${r.Master_ID}/bol`)}>BOL</Button>
            )}
            <Button size="small" onClick={() => openPdf(`/saleInvoices/${r.Master_ID}/scaleticket`)}>Scale Ticket</Button>
            <Button size="small" color="info" onClick={() => openPdf(`/saleInvoices/${r.Master_ID}/bundle`)}>Bundle ZIP</Button>
            {(r.InvoiceType || "Container") === "Container" && (
              <Button size="small" color="secondary" onClick={() => archivePhotos(r.Master_ID)}>Archive Photos</Button>
            )}
          </>
        );

        return isMobile ? (
          /* ---- Mobile: stacked cards ---- */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {filtered.length === 0 ? (
              <Typography align="center" sx={{ color: "text.secondary", py: 3 }}>No invoices found.</Typography>
            ) : filtered.map((r) => (
              <Paper key={r.Master_ID} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 700 }}>#{r.Master_ID}</Typography>
                  <Box>{typeChip(r)}{paymentChip(r.PaymentStatus)}</Box>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>{r.Name} · {r.Date?.split("T")[0]}</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "text.secondary" }}>
                  <span>Amount {(Number(r.TotalAmount) || 0).toFixed(2)}</span>
                  <span>Paid {(Number(r.PaidAmount) || 0).toFixed(2)}</span>
                  <span style={{ fontWeight: 700, color: (Number(r.Balance) || 0) > 0.01 ? "#d32f2f" : "inherit" }}>
                    Bal {(Number(r.Balance) || 0).toFixed(2)}
                  </span>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>{rowActions(r)}</Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.Master_ID}>
                    <TableCell>{r.Master_ID}</TableCell>
                    <TableCell>{r.Name}</TableCell>
                    <TableCell>{r.Date?.split("T")[0]}</TableCell>
                    <TableCell align="right">{(Number(r.TotalAmount) || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{(Number(r.PaidAmount)  || 0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: (Number(r.Balance) || 0) > 0.01 ? "error.main" : "inherit" }}>
                      {(Number(r.Balance) || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{paymentChip(r.PaymentStatus)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{rowActions(r)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{typeChip(r)}{statusChip(r)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      })()}
    </Box>
  );
}
